import React, { useRef, useCallback } from 'react';
import { message } from 'antd';
import { showMessage } from '../TankInformationSection/components/InfoMessage';
import { RECENTLY_UPDATED_HIGHLIGHT_DURATION } from './constants';

interface UpdateWithFeedbackOptions {
  setRecentlyUpdated: (data: any) => void;
  update: (data: any) => void;
  handleMessageClose: (messageKey?: string) => void;
}

interface UpdateWithFeedbackParams {
  recentlyUpdatedData: any;
  updateData: {
    isUpdatingTankInfo: boolean;
    field: any;
  };
  messageContent: string;
  messageKey?: string;
  onUndo?: () => void;
  skipRecentlyUpdated?: boolean; // New option to skip setRecentlyUpdated call
}

const useUpdateWithFeedback = ({
  setRecentlyUpdated,
  update,
  handleMessageClose
}: UpdateWithFeedbackOptions) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const executeUpdateWithFeedback = useCallback(
    ({
      recentlyUpdatedData,
      updateData,
      messageContent,
      messageKey = 'tank-info-updated',
      onUndo,
      skipRecentlyUpdated = false
    }: UpdateWithFeedbackParams) => {
      // Set recently updated fields (unless skipped)
      if (!skipRecentlyUpdated) {
        setRecentlyUpdated(recentlyUpdatedData);
      }

      // Call update
      update(updateData);

      // Clear any previous timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Close any existing messages
      message.destroy();

      // Generate unique message key to ensure each message is shown
      const uniqueMessageKey = `${messageKey}-${Date.now()}-${crypto.randomUUID()}`;

      // Create a close handler that knows about the specific message key
      const handleClose = () => {
        handleMessageClose(uniqueMessageKey);
      };

      // Create an undo handler that also closes the message
      const handleUndo = () => {
        if (onUndo) {
          onUndo();
        }
        handleMessageClose(uniqueMessageKey);
      };

      // Show message
      showMessage({
        content: messageContent,
        onUndo: onUndo ? handleUndo : undefined,
        onClose: handleClose,
        config: { key: uniqueMessageKey, icon: <></> }
      });

      // Create new timeout to reset isUpdatingTankInfo
      timeoutRef.current = setTimeout(() => {
        update({ isUpdatingTankInfo: false, field: null });
        timeoutRef.current = null;
      }, RECENTLY_UPDATED_HIGHLIGHT_DURATION);
    },
    [setRecentlyUpdated, update, handleMessageClose]
  );

  return { executeUpdateWithFeedback };
};

export default useUpdateWithFeedback;
